/**
 * monitorColors.ts — centralised brand palettes for the per-chain
 * monitor pages and the wallet tracker.
 *
 * V2.I.5.4: previously every monitor (CronosMonitor, HederaMonitor,
 * AlgorandMonitor, …) defined its own `*_COLORS` constant inline.
 * The wallet-tracker components duplicated `TRACKER_COLORS` across
 * AlertList / WalletList / WalletCard / AddWalletModal.
 *
 * Centralising :
 *   - kills the duplication for TRACKER_COLORS (4 → 1).
 *   - gives a single audit surface for brand consistency.
 *   - all entries satisfy the `NetworkColors` interface so the
 *     `MonitorTabs` / `NetworkMonitorPageHeader` primitives consume
 *     them without changes.
 *
 * Migration is incremental — chain monitors that haven't been
 * migrated yet still keep their inline `*_COLORS`, but new monitors
 * should import from here.
 */
import type { NetworkColors } from '../components/monitor/types';

/** Wallet-tracker palette (cyan-based). Was duplicated across 4 files. */
export const TRACKER_COLORS = {
  primary: '#06b6d4',
  secondary: '#0891b2',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  purple: '#8b5cf6',
} as const;

/** Chain monitor palettes. NetworkColors-compatible. */
export const CRONOS_COLORS: NetworkColors = {
  primary: '#1199FA',
  gradient: 'linear-gradient(135deg, #002D74, #1199FA)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 45, 116, 0.15), rgba(17, 153, 250, 0.1))',
};

export const HEDERA_COLORS: NetworkColors = {
  primary: '#000000',
  gradient: 'linear-gradient(135deg, #000000, #00C2FF)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 0, 0, 0.15), rgba(0, 194, 255, 0.1))',
};

export const ALGO_COLORS: NetworkColors = {
  primary: '#000000',
  gradient: 'linear-gradient(135deg, #000000, #2D2D2D)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 0, 0, 0.15), rgba(45, 45, 45, 0.1))',
};

export const ICP_COLORS: NetworkColors = {
  primary: '#522785',
  gradient: 'linear-gradient(135deg, #522785, #29ABE2)',
  lightGradient: 'linear-gradient(135deg, rgba(82, 39, 133, 0.15), rgba(41, 171, 226, 0.1))',
};

export const VET_COLORS: NetworkColors = {
  primary: '#15BDFF',
  gradient: 'linear-gradient(135deg, #15BDFF, #0084ff)',
  lightGradient: 'linear-gradient(135deg, rgba(21, 189, 255, 0.15), rgba(0, 132, 255, 0.1))',
};

export const ADA_COLORS: NetworkColors = {
  primary: '#0033AD',
  gradient: 'linear-gradient(135deg, #0033AD, #0099FF)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 51, 173, 0.15), rgba(0, 153, 255, 0.1))',
};

/** Account / Settings palette — also used by MyAccount and
 *  TaxStrategies (V2.I.bonus.b : TaxStrategies' inline TAX_COLORS was
 *  byte-identical to this — deduped). Indigo accent that re-uses the
 *  global app accent identity instead of introducing a new colour. */
export const ACCOUNT_COLORS: NetworkColors = {
  primary: '#6366F1',
  gradient: 'linear-gradient(135deg, #4F46E5, #6366F1)',
  lightGradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(79, 70, 229, 0.1))',
};

/** TaxCenter palette — distinct sky-blue identity (different from
 *  the indigo TaxStrategies guide). Kept separate for visual contrast
 *  between the strategy guide and the operational tax center. */
export const TAX_CENTER_COLORS: NetworkColors = {
  primary: '#0EA5E9',
  gradient: 'linear-gradient(135deg, #0284C7, #0EA5E9)',
  lightGradient: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(2, 132, 199, 0.1))',
};

export const AXELAR_COLORS: NetworkColors = {
  primary: '#37BAEC',
  gradient: 'linear-gradient(135deg, #37BAEC, #1976D2)',
  lightGradient: 'linear-gradient(135deg, rgba(55, 186, 236, 0.15), rgba(25, 118, 210, 0.1))',
};
